/**
 * Purpose: Owner-only management toolbar (visually distinct from visitor actions).
 *          Grows into the full Owner Dashboard in Phase 5 — new actions are
 *          appended here, never mixed into VisitorActionBar.
 * Inputs: business, onAddProduct, onAddService, addProductLabel.
 * Outputs: compact toolbar card; rendered ONLY when the viewer can manage the business.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CTAButton } from '../../ui';

const Icon = ({ d }) => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
  </svg>
);

const SHIELD_D = 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z';
const GRID_D =
  'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z';
const PENCIL_D =
  'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z';
const BOX_D = 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4';
const WRENCH_D =
  'M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26';

const OwnerActionBar = ({ business, onAddProduct, onAddService, addProductLabel = 'Add product' }) => {
  const navigate = useNavigate();

  return (
    <section
      aria-label="Owner tools"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-brand-200 bg-white px-3 py-2 dark:border-brand-900 dark:bg-gray-900"
    >
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-300">
        <Icon d={SHIELD_D} />
        Owner
      </span>

      <span className="hidden h-5 w-px bg-gray-200 dark:bg-gray-700 sm:block" aria-hidden />

      <div className="scrollbar-hide flex items-center gap-1.5 overflow-x-auto">
        <CTAButton
          size="sm"
          icon={<Icon d={GRID_D} />}
          onClick={() => navigate(`/businesses/${business.slug || business.id}/dashboard`)}
        >
          Dashboard
        </CTAButton>
        <CTAButton
          size="sm"
          variant="secondary"
          icon={<Icon d={PENCIL_D} />}
          onClick={() => navigate(`/businesses/${business.slug || business.id}/edit`)}
        >
          Edit business
        </CTAButton>
        {onAddProduct && (
          <CTAButton size="sm" variant="secondary" icon={<Icon d={BOX_D} />} onClick={onAddProduct}>
            {addProductLabel}
          </CTAButton>
        )}
        {onAddService && (
          <CTAButton size="sm" variant="secondary" icon={<Icon d={WRENCH_D} />} onClick={onAddService}>
            Add service
          </CTAButton>
        )}
      </div>
    </section>
  );
};

export default OwnerActionBar;
